/**
 * Role ranking for dashboard UI gating.
 *
 * Mirrors `backend/src/api/permissions.py`. The backend is the authority: every
 * mutating tenant endpoint enforces its own requirement, so nothing here grants
 * access. This exists so a viewer sees a disabled control with a reason instead
 * of a button that fails with a 403 after they have filled in a form.
 *
 * Where each role sits:
 *
 *   viewer   all reads; open and comment on their own support tickets
 *   editor + routing content — mappings, workflows, responses, templates,
 *            QR codes, assets
 *   admin  + domains, email routing, analytics purge, team management
 *   owner  + tenant lifecycle
 */

export type TenantRole = 'viewer' | 'editor' | 'admin' | 'owner';

export const ROLE_RANK: Record<TenantRole, number> = {
  viewer: 0,
  editor: 1,
  admin: 2,
  owner: 3,
};

/**
 * Whether `role` carries at least the authority of `required`.
 *
 * Fails closed for null/undefined (the tenants query has not resolved yet) and
 * for anything outside the vocabulary (e.g. a legacy `member` row). Showing
 * every action for the frame before the role loads, then taking them away, is
 * worse than showing none.
 */
export function hasAtLeast(
  role: TenantRole | null | undefined,
  required: TenantRole
): boolean {
  if (!role || !(role in ROLE_RANK)) return false;

  return ROLE_RANK[role] >= ROLE_RANK[required];
}

/** Human-readable reason for a disabled control's tooltip. */
export function insufficientRoleReason(
  role: TenantRole | null | undefined,
  required: TenantRole
): string {
  return `Requires the ${required} role or higher — yours is ${role ?? 'not loaded'}.`;
}

/**
 * Navigation entries the caller may see. An entry without `minRole` is for
 * everyone; one with it is hidden until the role is known and high enough, so
 * a viewer never sees a link to a page that would only say "no access".
 */
export function visibleForRole<T extends { minRole?: TenantRole }>(
  items: readonly T[],
  role: TenantRole | null | undefined
): T[] {
  return items.filter((item) => !item.minRole || hasAtLeast(role, item.minRole));
}
